import { HttpResponse }       from '@angular/common/http';
import { SecurityContext }    from '@angular/core';
import { DomSanitizer }       from '@angular/platform-browser';
import { DownloadFormatEnum } from '@shared/components/download-button/download-button.model';
import {
	EnumUtils,
	NumberUtils,
	ObjectUtils
}                             from '@utils/helpers/index';

export enum FilesizeShortUnitEnum {
	BYTE = 'Byte',
	KB = 'KB',
	MB = 'MB',
	GB = 'GB',
	TB = 'TB',
	PB = 'PB',
	EB = 'EB',
	ZB = 'ZB',
	YB = 'YB'
}

export enum FilesizeLongUnitEnum {
	BYTE = 'Byte',
	KILOBYTE = 'Kilobyte',
	MEGABYTE = 'Megabyte',
	GIGABYTE = 'Gigabyte',
	TERABYTE = 'Terabyte',
	PETABYTE = 'Petabyte',
	EXABYTE = 'Exabyte',
	ZETTABYTE = 'Zettabyte',
	YOTTABYTE = 'Yottabyte'
}


/**
 * Creates a text file and triggers its download.
 * @param fileContent A string that constitutes the actual content of the file.
 * @param filename The file name **without** the file extension.
 * @param fileFormat The format of the file content. This will have effect on
 *        the MIME subtype and the filename extension.
 * @param sanitizer An instance of the Angular DomSanitizer. This is an optional
 *        parameter that, if provided, prevents Angular's DomSanitizer from
 *        auto-removing parts of the Data URL.
 */
function downloadTextFile(
	fileContent: string,
	filename: string,
	fileFormat: DownloadFormatEnum,
	sanitizer?: DomSanitizer
): void {
	if ( !fileContent || !filename ) { return; }
	fileFormat = fileFormat ?? DownloadFormatEnum.TXT;

	const mimeSubtype = [ DownloadFormatEnum.KEY, DownloadFormatEnum.TXT ].includes( fileFormat ) ? 'plain' : fileFormat;
	const link = document.createElement( 'a' );
	const dataUrl = `data:text/${ mimeSubtype };charset=UTF-8,` + encodeURIComponent( fileContent );
	link.href = sanitizer
							? sanitizer.sanitize( SecurityContext.URL, sanitizer.bypassSecurityTrustUrl( dataUrl ) )
							: dataUrl;
	link.download = `${ filename }.${ fileFormat }`;
	link.click();
	link.remove();
}

/**
 * FOR UNIT TESTS ONLY!
 * Do not import anywhere else than in file-utils.spec.ts!
 */
export const UT = {
	downloadTextFile
};


/**
 * Automatically converts a given data volume in bytes to a more appropriate
 * prefixed unit, such as Megabytes or Gigabytes. The conversion is done
 * in a way that the resulting number is greater than or equal to 1 and smaller
 * than 1000 (e.g. 123,456,789 Bytes would be converted into 123.456789 MB,
 * because this is greater than 1 MB and smaller than 1000 MB). Upper limit
 * of this behavior is the Yottabayte range, i.e. values from 10^27 on will
 * always be converted to Yottabytes.
 *
 * NOTE that the conversion is DECIMAL, i.e. 1 KB = 1000 Bytes, 1 MB = 1,000,000 Bytes etc.!
 * (user-friendly, intuitive conversion factor)
 *
 * @param bytes The data volume to be converted
 * @param round Optional parameter, allowing for rounding of the converted value
 *              to the nearest integer.
 */
function convertBytesToPrefixedUnit( bytes: number, round = false ): number {
	if ( !bytes || bytes < 0 || NumberUtils.isNanOrInfinity( bytes ) ) { return null; }
	const prefixedUnit = getPrefixedUnitByFilesize( bytes );
	const units = EnumUtils.getEnumsValuesAsList( FilesizeShortUnitEnum );
	const index = units.indexOf( prefixedUnit.short );

	const size = +( bytes / Math.pow( 10, index * 3 ) ).toPrecision( 20 );

	return round
				 ? Math.round( size )
				 : size;
}


function downloadBlobFile( blob: Blob, filename: string ): void {
	if ( !blob || !filename ) { return; }
	const data = window.URL.createObjectURL( blob );
	const link = document.createElement( 'a' );
	link.href = data;
	link.download = filename;
	link.click();
	link.remove();
	setTimeout( () => {
		// For Firefox, it is necessary to delay revoking the ObjectURL
		window.URL.revokeObjectURL( data );
	}, 400 );
}


function downloadKeyFile( key: string ): void {
	if ( !key ) { return; }

	const privateKeyContent: string = '-----BEGIN PRIVATE KEY-----\n' +
																		key.replace( /(.{64})/g, '$1\n' ) +
																		'\n-----END PRIVATE KEY-----\n';

	downloadTextFile( privateKeyContent, 'private', DownloadFormatEnum.KEY );
}


function downloadFileAsCsv( csvString: string, filename: string, sanitizer: DomSanitizer ): void {
	downloadTextFile( csvString, filename, DownloadFormatEnum.CSV, sanitizer );
}


function downloadFileAsJson( object: object, filename: string, sanitizer: DomSanitizer, beautify?: boolean ): void {
	if ( ObjectUtils.isEmpty( object ) || !filename ) { return; }
	const jsonString = JSON.stringify( object, null, beautify ? 2 : null );
	downloadTextFile( jsonString, filename, DownloadFormatEnum.JSON, sanitizer );
}


/**
 * Extracts the name of a downloaded file from the response header.
 * By default, the `content-disposition` response header together with the RegEx
 * pattern `filename="(.*)"` is used, but you can provide custom values via the
 * `options` parameter.
 * In case you are providing a custom RegExp, make sure that it either has
 * exactly one capturing group for the file name or that it captures the file
 * name by the first capturing group. Otherwise, the function will return either
 * the wrong string or the fallback file name.
 */
function extractFileNameFromResponseHeader(
	options: {
		fallbackFilename: string,
		httpResponse: HttpResponse<any>
		regExp?: RegExp,
		responseHeaderName?: string
	}
): string {
	const {
		fallbackFilename,
		httpResponse,
		regExp,
		responseHeaderName
	} = options;

	const matchResult: string[] = ( regExp ?? /filename="(.*)"/ ).exec(
		httpResponse?.headers.get( responseHeaderName ?? 'content-disposition' )
	);

	return matchResult?.[ 1 ] ?? fallbackFilename;
}


/**
 * Checks if the size limit for a file is exceeded.
 *
 * NOTE: This function uses BINARY conversion, i.e. 1 KB = 1024 Bytes,
 * 1 MB = 1,048,576 Bytes etc.!
 * (more appropriate conversion factor regarding backend requirements)
 *
 * @param fileSizeInBytes
 * @param limit The size limit
 * @param limitUnit The reference unit of the specified size limit
 * @param decimals Optional value for setting the precision level. Default value
 * is 2.
 */
function isFilesizeAboveLimit(
	fileSizeInBytes: number,
	limit: number,
	limitUnit: FilesizeShortUnitEnum,
	decimals = 2
): boolean {

	const sizes: FilesizeShortUnitEnum[] = EnumUtils.getEnumsValuesAsList( FilesizeShortUnitEnum ) as FilesizeShortUnitEnum[];

	if (
		!fileSizeInBytes || NumberUtils.isNanOrInfinity( fileSizeInBytes ) || fileSizeInBytes < 0 ||
		ObjectUtils.isNullish( limit ) || NumberUtils.isNanOrInfinity( limit ) || limit < 0
	) { return false; }
	if ( !sizes.includes( limitUnit ) ) { return false; }

	const dm = decimals ?? 0;

	const i = sizes.findIndex( size => size.toUpperCase() === limitUnit.toUpperCase() );
	const convertedSize = parseFloat( (
		fileSizeInBytes / Math.pow( 2, i * 10 )
	).toFixed( dm ) );
	return convertedSize > limit;
}

/**
 * Returns a more appropriate prefixed unit (such as KB, MB, GB etc.) for a
 * given data volume in Bytes, so that conversion of that number would result in
 * a number that is greater than or equal to 1 and smaller than 1000
 * (e.g. 123,456,789 Bytes would result in MB being returned as unit, because
 * 123.46... MB are greater than 1 MB and smaller than 1000 MB). Upper limit
 * of this behavior is the Yottabayte range, i.e. values from 10^27 on will
 * always be converted to Yottabytes.
 *
 * NOTE that the underlying conversion is DECIMAL, i.e. 1 KB = 1000 Bytes,
 * 1 MB = 1,000,000 Bytes etc.!
 * (user-friendly, intuitive conversion factor)
 *
 * @param filesize Size (of a file or something else) in Bytes
 * @returns A plain object containing the long and short identifier of the resulting unit.
 */
function getPrefixedUnitByFilesize( filesize: number ): { short: FilesizeShortUnitEnum; long: FilesizeLongUnitEnum; } {
	if ( !filesize || filesize < 0 || NumberUtils.isNanOrInfinity( filesize ) ) { return null; }

	const exponent: number = Math.floor( Math.log10( filesize ) );

	let longUnit: FilesizeLongUnitEnum;
	let shortUnit: FilesizeShortUnitEnum;

	if ( NumberUtils.numberIsInRange( exponent, 0, 2, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.BYTE;
		shortUnit = FilesizeShortUnitEnum.BYTE;
	} else if ( NumberUtils.numberIsInRange( exponent, 3, 5, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.KILOBYTE;
		shortUnit = FilesizeShortUnitEnum.KB;
	} else if ( NumberUtils.numberIsInRange( exponent, 6, 8, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.MEGABYTE;
		shortUnit = FilesizeShortUnitEnum.MB;
	} else if ( NumberUtils.numberIsInRange( exponent, 9, 11, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.GIGABYTE;
		shortUnit = FilesizeShortUnitEnum.GB;
	} else if ( NumberUtils.numberIsInRange( exponent, 12, 14, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.TERABYTE;
		shortUnit = FilesizeShortUnitEnum.TB;
	} else if ( NumberUtils.numberIsInRange( exponent, 15, 17, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.PETABYTE;
		shortUnit = FilesizeShortUnitEnum.PB;
	} else if ( NumberUtils.numberIsInRange( exponent, 18, 20, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.EXABYTE;
		shortUnit = FilesizeShortUnitEnum.EB;
	} else if ( NumberUtils.numberIsInRange( exponent, 21, 23, { minMaxIsIncluded: true } ) ) {
		longUnit = FilesizeLongUnitEnum.ZETTABYTE;
		shortUnit = FilesizeShortUnitEnum.ZB;
	} else {
		longUnit = FilesizeLongUnitEnum.YOTTABYTE;
		shortUnit = FilesizeShortUnitEnum.YB;
	}

	return { short: shortUnit, long: longUnit };
}

/**
 * Automatically converts a given data volume in bytes to a more appropriate
 * prefixed unit, such as Megabytes or Gigabytes. The converted value is rounded
 * to the nearest integer and returned as a string along with its corresponding
 * unit.
 * The conversion is done in a way that the resulting number is greater than or
 * equal to 1 and smaller than 1000 (e.g. 123,456,789 Bytes would be converted
 * into 123.456789 MB, because this is greater than 1 MB and smaller than
 * 1000 MB). Upper limit of this behavior is the Yottabayte range, i.e. values
 * from 10^27 on will always be converted to Yottabytes.
 *
 * NOTE that the conversion is DECIMAL, i.e. 1 KB = 1000 Bytes, 1 MB = 1,000,000 Bytes etc.!
 * (user-friendly, intuitive conversion factor)
 *
 * @param filesize The data volume to be converted.
 * @param useShortUnitFormat An optional flag that, if true, will set the
 *                           prefixed unit as the long form (i.e. Megabyte
 *                           rather than MB)
 */
function getPrettyFilesize( filesize: number, useShortUnitFormat: boolean = true ): string {
	if ( !filesize || filesize < 0 || NumberUtils.isNanOrInfinity( filesize ) ) { return null; }

	const prefixedUnit = getPrefixedUnitByFilesize( filesize );
	const unitFormat = useShortUnitFormat ? prefixedUnit.short : prefixedUnit.long;

	const units = EnumUtils.getEnumsValuesAsList( FilesizeShortUnitEnum );
	const index = units.indexOf( prefixedUnit.short );

	const size = `${ Math.round( filesize / Math.pow( 10, index * 3 ) ) } ${ unitFormat }`;

	return size;
}


function replaceFileExtension( filename: string, newFileExtension: string ): string {
	if ( !filename || !newFileExtension ) { return filename; }
	return filename.replace( /\.([^.]+)$/, `.${ newFileExtension }` ).toLowerCase();
}

/**
 * Sanitizes a string or an actual file name so that it can be safely used as
 * a file name.
 * The default sanitization will
 * * remove characters which are not allowed in file names in Windows and UNIX-based OS
 * * replace whitespaces with dashes
 * * strip diacritics and normalize subscripts, superscripts, and ligatures
 * * and append an underscore if the (sanitized) file name is a reserved name in Windows.
 * @param name The string to be sanitized.
 * @param options Some options for fine-tuning the function's behavior.
 * @param options.customRegExp A string representing a regular expression that
 *                             will replace the RegExp used for default sanitization.
 *                             Note that this has to be a plain string, and be
 *                             aware of the peculiarities of RegExp strings in
 *                             contrast to RegExp literals. The flags used by
 *                             the function are 'gu' and cannot be changed.
 * @param options.maxLength The maximum length of the file name, **including the
 *                          file extension**. Default length is 255 chars.
 * @param options.preserveExtension If `true` the file extension on the input
 *                                  string will be kept (default behavior), if
 *                                  `false`, the file extension will be removed.
 * @param options.replacement A custom replacement string for stripped characters.
 *                            By default, stripped characters will be replaced by a dash.
 */
function sanitizeFilename(
	name: string,
	options?: {
		customRegExp?: string;
		maxLength?: number;
		preserveExtension?: boolean;
		replacement?: string;
	}
): string {
	if ( !name ) return 'unnamed';

	const {
		maxLength = 255,
		preserveExtension = true
	} = options || {};

	let {
		customRegExp = '[\x00-\x1F<>:"/\\|?*]',
		replacement = '-'
	} = options || {};
	customRegExp = ( customRegExp === null || customRegExp === '' ) ? '[]' : customRegExp;
	replacement = replacement ?? '';

	/*
	 * String.prototype.normalize() returns the "Unicode Normalization Form" of a
	 * string, i.e. it "decouples" base letters from their diacritics, removes
	 * superscripts (² -> 2) and "dissolves" ligatures (ĳ -> ij).
	 * Then String.prototype.replace() can strip only the diacritics and
	 * preserve the base letters (i.e. ü -> u, ř -> r etc.)
	 */
	name = name.normalize( 'NFKD' ).replace( /[\u0300-\u036f]/g, '' );

	// Replace spaces with the whitespace replacement character
	name = name.trim().replace( /\s+/g, '-' );

	// Define a regex pattern that identifies invalid characters excluding already addressed whitespace
	const invalidChars = new RegExp( customRegExp, 'gu' );

	// Replace invalid characters with the specified replacement character
	let sanitized = name.replace( invalidChars, replacement );

	if ( sanitized.length && replacement.length ) {
		// Trim leading and trailing replacement characters and/or leading and trailing dots
		// !SonaQube: potential ReDoS (Regular Expression Denial of Service) for /(^[-.]+)|([-.]+$)/g
		while ( sanitized.startsWith( replacement ) || sanitized.startsWith( '.' ) ) {
			sanitized = sanitized.slice( sanitized.startsWith( '.' ) ? 1 : replacement.length );
		}

		while ( sanitized.endsWith( replacement ) || sanitized.endsWith( '.' ) ) {
			sanitized = sanitized.slice( 0, sanitized.endsWith( '.' ) ? -1 : -replacement.length );
		}
	}

	// If filename becomes empty, use a default name
	if ( !sanitized ) {
		sanitized = 'sanitized';
	}

	// Handle file extension
	let extension = '';

	const extMatch = preserveExtension
									 ? /(\.[a-zA-Z0-9]+)$/.exec( sanitized )
									 : null;

	if ( extMatch ) {
		extension = extMatch[ 0 ];
		sanitized = sanitized.slice( 0, -extension.length );
	} else if ( !preserveExtension ) {
		const i = sanitized.lastIndexOf( '.' );
		if ( i > 0 ) sanitized = sanitized.slice( 0, i );
	}

	sanitized = sanitized.slice( 0, maxLength - extension.length );
	sanitized += extension;

	// Avoid reserved Windows names by appending an underscore if needed
	const reservedNames = [
		'CON', 'PRN', 'AUX', 'NUL',
		'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
		'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
	];

	if ( reservedNames.includes( sanitized.toUpperCase() ) ) {
		sanitized += '_';
	}

	return sanitized;
}


export const publicFunctions = {
	convertBytesToPrefixedUnit,
	downloadBlobFile,
	downloadFileAsCsv,
	downloadFileAsJson,
	downloadKeyFile,
	extractFileNameFromResponseHeader,
	isFilesizeAboveLimit,
	getPrefixedUnitByFilesize,
	getPrettyFilesize,
	replaceFileExtension,
	sanitizeFilename
};
